import { Flex } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { RowSelectionState } from "@tanstack/react-table";
import { Endpoints } from "api";
import { Button, Search, Table, ViewContainer } from "components";
import { useState } from "react";
import { FiEdit } from "react-icons/fi";
import { useNavigate } from "react-router";
import { useDownloadFile } from "utils";
import { useScreen } from "utils/useScreen";
import { CreateAudienceDefinition } from "../CreateAudience/CreateAudience.definition";
import { CatalogManagerScreenDefinition } from "./CatalogManager.definition";
import { listAudiences } from "./CatalogManager.requests";
import { columns } from "./components/CatalogTable.columns";

const CatalogManager = () => {
  useScreen(CatalogManagerScreenDefinition);
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const navigate = useNavigate();
  const { download } = useDownloadFile();

  const { isFetching, data } = useQuery(
    [`catalog-manager`],
    () => listAudiences(),
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 30 * 60 * 1000, // 30 minutes
    }
  );

  const filteredData = searchValue
    ? (data || []).filter((audience) =>
        audience.fullSegmentName
          .toLowerCase()
          .includes(searchValue.toLowerCase())
      )
    : data || [];

  const handleExportClick = () => {
    if (!data) {
      return;
    }

    download({
      url: Endpoints.Audience.Export,
      fileName: "audiences",
      fileExtension: "zip",
      method: "POST",
      data: {
        names: rowSelection,
      },
    });
  };

  return (
    <ViewContainer>
      <Flex
        w="100%"
        justifyContent="space-between"
        marginY={4}
        alignItems="center"
      >
        <Search
          currentValue={searchValue}
          onChange={(v) => {
            setSearchValue(v);
          }}
          mb={0}
        />
        <Flex>
          <Button
            variant="primary-teal"
            rightIcon={<FiEdit />}
            size="sm"
            onClick={() => {
              navigate(CreateAudienceDefinition.navigate());
            }}
          >
            Create Audience
          </Button>
          <Button
            variant="outline-teal"
            ml={5}
            isDisabled={Object.values(rowSelection).length == 0}
            onClick={handleExportClick}
          >
            Export
          </Button>
        </Flex>
      </Flex>
      <Table
        variant="intentsifyNew"
        defaultSort={[{ id: "displayName", desc: true }]}
        columns={columns}
        isFetching={isFetching}
        data={filteredData}
        resetPaginationDependencies={[searchValue]}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
      />
    </ViewContainer>
  );
};

export { CatalogManager };
